/**
 * Note: Before developing this content, you must first define the theme token values of light and dark of the component.
 */
import { generateDynamicThemeToken } from '../../ThemeProvider';
import { getThemeToken } from './theme';
import { randomStr } from '../../Shared';
const componentName = 'ScrollView';
const classPrefix = `ui-ScrollView`;
/**
 * The classes here are the independent classes of the react-ui component.
 * It is mainly isolated from the classes of the mui component.
 * Avoid style conflicts when mui and react-ui are used simultaneously in project applications.
 *
 * On the way to develop react-ui components based on mui,
 * you must use this react-ui classes instead of mui classes.
 * You can control the built-in classes of the mui component through the classes prop of mui component.
 */
export const scrollViewClasses = {
    // It is recommended that the root classname of the react-ui component be added with a random string.
    // This is mainly to avoid classname naming conflicts in the application
    root: `${classPrefix}-root-${randomStr()}`,
    scroller: `${classPrefix}-scroller`,
    content: `${classPrefix}-content`,
    track: `${classPrefix}-track`,
    thumb: `${classPrefix}-thumb`,
    horizontal: `${classPrefix}-horizontal`,
    vertical: `${classPrefix}-vertical`,
    active: `${classPrefix}-active`,
};
/**
 * All style css codes of react-ui components are centrally managed in this method through classes.
 * Through this method, the light and dark themes of the components can be controlled.
 * You don't need to care about the implementation of theme switching.
 * You only need to define the specified style value based on the content of the token.
 */
export function getStyle(theme) {
    const dynamicToken = getThemeToken(theme);
    // generateThemeToken will return two contents, one is token data, and the other is tk method.
    // The token includes the light and dark theme style values defined by this component,
    // and it will automatically switch the token content of light or dark according to the outer ThemeProvider.
    // The role of tk is to process custom theme configuration,
    // and override the application custom token to the default light or dark token.
    const [token, tk] = generateDynamicThemeToken({
        componentName,
        theme,
        dynamicToken,
    });
    const style = {
        [`&.${scrollViewClasses.root}`]: {
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'stretch',
            [`&:hover > .${scrollViewClasses.track}, &.${scrollViewClasses.active} > .${scrollViewClasses.track}`]: {
                opacity: 1,
                transition: 'background .3s ease-out, border-color .3s ease-out, opacity .2s ease-out',
            },
            [`.${scrollViewClasses.scroller}`]: {
                overflow: 'hidden',
                flex: '1 1 auto',
                minWidth: 0,
                scrollbarWidth: 'none',
                position: 'relative',
                zIndex: 0,
                [`&::scrollbar`]: {
                    display: 'none',
                },
                [`&::-webkit-scrollbar`]: {
                    display: 'none',
                },
                [`&.${scrollViewClasses.horizontal}`]: {
                    overflowX: 'auto',
                },
                [`&.${scrollViewClasses.vertical}`]: {
                    overflowY: 'auto',
                },
            },
            [`.${scrollViewClasses.track}`]: {
                position: 'absolute',
                right: 0,
                bottom: 0,
                boxSizing: 'border-box',
                transition: 'background .3s ease-out, border-color .3s ease-out, opacity .5s',
                opacity: 0,
                border: '0px solid transparent',
                [`&.${scrollViewClasses.horizontal}`]: {
                    left: 0,
                    height: tk(token.track.width),
                    borderTopWidth: 1,
                },
                [`&.${scrollViewClasses.vertical}`]: {
                    top: 0,
                    width: tk(token.track.width),
                    borderLeftWidth: 1,
                },
                // We don't want to bother users with a touchscreen or other coarse pointers,
                // since it's preferred to scroll on the entire area, instead of the thin bar
                [`@media (pointer: coarse)`]: {
                    pointerEvents: 'none',
                },
            },
            [`.${scrollViewClasses.thumb}`]: {
                position: 'absolute',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    background: tk(token.thumb.background.default),
                    borderRadius: 99,
                },
                [`&.${scrollViewClasses.horizontal}`]: {
                    top: 0,
                    height: '100%',
                    '&::before': {
                        left: 0,
                        right: 0,
                        top: '50%',
                        height: tk(token.thumb.width.default),
                        transform: 'translateY(-50%)',
                        transition: 'height .25s ease-out',
                    },
                },
                [`&.${scrollViewClasses.vertical}`]: {
                    left: 0,
                    width: '100%',
                    '&::before': {
                        top: 0,
                        bottom: 0,
                        left: '50%',
                        width: tk(token.thumb.width.default),
                        transform: 'translateX(-50%)',
                        transition: 'width .25s ease-out',
                    },
                },
                [`&:active::before, &.${scrollViewClasses.active}::before`]: {
                    background: tk(token.thumb.background.active),
                },
            },
        },
        [`.${scrollViewClasses.track}:hover, .${scrollViewClasses.track}.${scrollViewClasses.active}`]: {
            transition: 'background .2s ease-out, border-color .2s ease-out, opacity .5s 1s',
            background: tk(token.track.background),
            borderColor: token.track.border,
            [`.${scrollViewClasses.thumb}`]: {
                [`&.${scrollViewClasses.horizontal}::before`]: {
                    height: tk(token.thumb.width.hover),
                },
                [`&.${scrollViewClasses.vertical}::before`]: {
                    width: tk(token.thumb.width.hover),
                },
            },
        },
    };
    return style;
}
